/**
 * Gemini API 호출 유틸리티
 * 프로덕션: nginx가 /api/gemini 를 opc1 API 서버로 프록시
 */

interface GeminiAnalysisRequest {
  userAnswer: string;
  sampleAnswer: string;
  question?: string;
  analysisType?: 'similarity' | 'grammar' | 'improvement' | 'ielts-writing' | 'interview-answer';
  images?: Array<{
    data: string;
    mimeType: string;
  }>;
}

interface GeminiAnalysisResponse {
  success: boolean;
  analysis?: any;
  analysisType?: string;
  error?: string;
  rawText?: string;
}

export interface GeminiModelStatus {
  configuredModel: string;
  apiVersion: string;
  usable: boolean;
  listed: boolean;
  probeOk: boolean;
  message: string;
  error?: string;
  account?: string;
  projectId?: string;
  projectNumber?: string;
  billingEnabled: boolean;
  keyHint?: string;
  checkedAt: string;
}

const GEMINI_API_PATH = '/api/gemini';

async function callGeminiApi(request: GeminiAnalysisRequest): Promise<GeminiAnalysisResponse> {
  const response = await fetch(GEMINI_API_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      if (data && typeof data.error === 'string' && data.error.trim()) {
        message = data.error;
      }
    } catch (parseError) {
      // 본문이 JSON이 아니면 상태 코드만 보여 줍니다.
    }
    throw new Error(message);
  }

  return await response.json();
}

/**
 * 프롬프트 생성
 */
function generatePrompt(request: GeminiAnalysisRequest): string {
  const { userAnswer, sampleAnswer, question, analysisType = 'similarity' } = request;

  switch (analysisType) {
    case 'ielts-writing':
      return `You are an IELTS Academic Writing examiner. Evaluate ONLY Writing. Do NOT mention Speaking.

Task Info:
${question ? `${question}\n` : ''}
Sample Answer:
${sampleAnswer}

Student Answer:
${userAnswer}

Rules:
1) Respond ONLY with valid JSON (no markdown).
2) All text must be in Korean (한국어).
3) Use IELTS Writing criteria.
4) If the answer is extremely short (e.g., "hello"), state it does not address the writing task and score accordingly.

JSON:
{
  "overallScore": <0-100>,
  "bandScore": <0-9>,
  "bandBreakdown": {
    "taskResponse": <0-9>,
    "coherenceCohesion": <0-9>,
    "lexicalResource": <0-9>,
    "grammar": <0-9>
  },
  "guideAdherence": {
    "score": <0-100>,
    "summary": "<Korean>",
    "issues": ["<Korean>", "<Korean>"]
  },
  "strengths": ["<Korean>", "<Korean>"],
  "improvements": ["<Korean>", "<Korean>"],
  "suggestions": ["<Korean>", "<Korean>"],
  "recommendedVocabulary": ["<Korean or English vocab/idiom>", "<Korean or English vocab/idiom>"],
  "feedback": "<Korean. Do not mention speaking.>"
}`;
    case 'similarity':
      // TEF Canada Writing인지 확인 (question에 "TEF" 또는 "Expression Écrite" 또는 "Fait Diver" 또는 "Letters"가 포함되어 있는지)
      const isTEFCanada = question && (
        question.includes('TEF') || 
        question.includes('Expression Écrite') || 
        question.includes('Fait Diver') || 
        question.includes('Letters') ||
        question.includes('Section A') ||
        question.includes('Section B')
      );
      
      const evaluatorRole = isTEFCanada 
        ? 'TEF Canada writing test evaluator (Expression Écrite)' 
        : 'IELTS speaking test evaluator';
      
      const questionLabel = isTEFCanada 
        ? 'TEF Canada Writing Question' 
        : 'IELTS Speaking Question';
      
      return `You are a ${evaluatorRole}. Compare the following two answers and provide a detailed analysis.

Question: ${question || questionLabel}

Sample Answer: ${sampleAnswer}

Student Answer: ${userAnswer}

IMPORTANT: 
1. You must respond ONLY with valid JSON. Do not include any markdown formatting, code blocks, or explanatory text. Only return the JSON object.
2. All text in the JSON response must be in Korean (한국어). Write all analysis, differences, strengths, improvements, and suggestions in Korean.

Required JSON structure:
{
  "similarityScore": <number between 0-100>,
  "keyDifferences": ["<difference1 in Korean>", "<difference2 in Korean>", ...],
  "strengths": ["<strength1 in Korean>", "<strength2 in Korean>", ...],
  "improvements": ["<improvement1 in Korean>", "<improvement2 in Korean>", ...],
  "suggestions": ["<suggestion1 in Korean>", "<suggestion2 in Korean>", ...],
  "recommendedVocabulary": ["<Korean or English vocab/idiom>", "<Korean or English vocab/idiom>", ...]
}

Provide:
1. Similarity score (0-100): How similar is the student's answer to the sample answer?
2. Key differences: List 3-5 main differences between the two answers (in Korean)
3. Strengths: List 2-3 strengths of the student's answer (in Korean)
4. Improvements: List 3-5 areas where the student can improve (in Korean)
5. Suggestions: List 3-5 specific, actionable suggestions (in Korean)
6. Recommended vocabulary/idioms the student should use (in Korean or English)

Remember: Respond ONLY with valid JSON in Korean, nothing else.`;

    case 'grammar':
      return `Analyze the grammar and language accuracy of this IELTS speaking answer:

Answer: ${userAnswer}

IMPORTANT: All text in the JSON response must be in Korean (한국어). Write all analysis, errors, corrections, and feedback in Korean.

Provide:
1. Grammar errors found (in Korean)
2. Vocabulary usage assessment (in Korean)
3. Sentence structure evaluation (in Korean)
4. Overall language accuracy score (0-100)

Format as JSON:
{
  "grammarErrors": [{"error": "<error in Korean>", "correction": "<correction in Korean>"}, ...],
  "vocabularyScore": <number>,
  "sentenceStructureScore": <number>,
  "overallScore": <number>,
  "feedback": "<overall feedback in Korean>"
}

Remember: Respond ONLY with valid JSON in Korean, nothing else.`;

    case 'improvement':
      return `Provide specific improvement suggestions for this IELTS speaking answer:

Question: ${question || 'IELTS Speaking Question'}
Answer: ${userAnswer}

IMPORTANT: All text in the JSON response must be in Korean (한국어). Write all suggestions in Korean.

Provide detailed suggestions for:
1. Content (ideas, examples, details) - in Korean
2. Language (vocabulary, grammar, fluency) - in Korean
3. Structure (organization, coherence) - in Korean

Format as JSON:
{
  "contentSuggestions": ["<suggestion1 in Korean>", ...],
  "languageSuggestions": ["<suggestion1 in Korean>", ...],
  "structureSuggestions": ["<suggestion1 in Korean>", ...]
}

Remember: Respond ONLY with valid JSON in Korean, nothing else.`;

    default:
      return `Analyze this IELTS speaking answer: ${userAnswer}`;
  }
}

export function buildGeminiPrompt(request: GeminiAnalysisRequest): string {
  return generatePrompt(request);
}

export async function analyzeWithGemini(
  request: GeminiAnalysisRequest
): Promise<GeminiAnalysisResponse> {
  return await callGeminiApi(request);
}

export async function listGeminiModels(): Promise<Record<string, unknown>> {
  const response = await fetch(GEMINI_API_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'listModels' })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success || !data.models) {
    throw new Error(data.error || 'Failed to fetch models');
  }

  return data.models;
}

export async function fetchGeminiStatus(): Promise<GeminiModelStatus> {
  const response = await fetch(GEMINI_API_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'status' })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success || !data.status) {
    throw new Error(data.error || 'Failed to fetch Gemini status');
  }

  return data.status as GeminiModelStatus;
}

export function formatAnalysisError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message;
    if (message.includes('high demand')) {
      return 'Gemini API가 일시적으로 혼잡합니다. 잠시 후 다시 시도해 주세요.';
    }
    if (message.startsWith('HTTP error')) {
      return `서버 연결 오류가 발생했습니다. (${message})`;
    }
    return message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return 'AI 분석 중 알 수 없는 오류가 발생했습니다.';
}

export function getAnalysisErrorMessage(data: GeminiAnalysisResponse): string | null {
  if (data.success && data.analysis) {
    return null;
  }

  return data.error || 'AI 분석에 실패했습니다.';
}
